import { useCallback, useEffect, useState } from "react";
import { useContextStateManagement } from "../context/ContextApi";
import { getUserLicenseDetails } from "../Variables";
import { DynamicLeadForm } from "./DynamicLeadForm";
import { DynamicBulkUpload } from "./DynamicBulkUpload";
import { PracticeSchemaEditor } from "../practiceSchema/PracticeSchemaEditor";

// Merges the old "Bulk Upload" (bulk-upload-lead/BulkUploadLead.js's leadGTab branch) and
// "Upload Lead" (upload-lead-form/LeadGForm.js) pages into one schema-driven page, for
// activeTab === "leadGTab" only — every other tab keeps using the original BulkUploadLead
// component untouched (see Home.js's render-site condition).
//
// PracticeId comes from the same context every other page in this console already uses (tied to
// the "Select Organization" dropdown) — no new scoping logic needed, this page is automatically
// scoped to whichever practice/organization the user currently has selected.
export const ManageLeadsPage = ({ outBoundLeadsDetail, setOutBoundLeadsDetail }) => {
  const { PracticeId, token, authorizedGetRequest, usersPostRequest, fetchUserRecord, userType } =
    useContextStateManagement();

  const [loading, setLoading] = useState(true);
  const [hasSchema, setHasSchema] = useState(false);
  const [schemaFields, setSchemaFields] = useState([]);
  const [providerData, setProviderData] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [resultMsg, setResultMsg] = useState("");
  const [showSchemaEditor, setShowSchemaEditor] = useState(false);

  const loadSchemaAndProviders = useCallback(async () => {
    if (!PracticeId) return;
    setLoading(true);
    try {
      const [schemaResponse, providerResponse] = await Promise.all([
        authorizedGetRequest(token, `/api/PracticeSchemaVersion/${PracticeId}`),
        fetchUserRecord(token, `/api/LeadGenerationOnboarding/${PracticeId}`),
      ]);
      setHasSchema(!!schemaResponse?.schema);
      setSchemaFields(schemaResponse?.schema?.Fields ?? []);
      setProviderData(providerResponse?.providersData || []);
    } finally {
      setLoading(false);
    }
    // authorizedGetRequest/fetchUserRecord intentionally excluded: ContextApi.js doesn't memoize
    // its functions (no useCallback anywhere in that provider), so including them would refetch
    // on every unrelated context re-render instead of only when PracticeId/token actually change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PracticeId, token]);

  useEffect(() => {
    loadSchemaAndProviders();
  }, [loadSchemaAndProviders]);

  // Called once a schema is successfully published, so the "no schema yet" gate (or the freshly
  // updated field list) reflects it immediately without needing a manual page refresh.
  const handleSchemaPublished = () => {
    setShowSchemaEditor(false);
    loadSchemaAndProviders();
  };

  const computeLeadsAllowed = () => {
    if (userType === "admin" || outBoundLeadsDetail === undefined) return "";
    if (outBoundLeadsDetail.LicenseType !== "trial") return "";
    return outBoundLeadsDetail.LeadsAllowed - parseInt(outBoundLeadsDetail.LeadGenerationUploadLead, 10);
  };

  const submitLeads = async (leadsArray) => {
    setSubmitting(true);
    setResultMsg("");
    try {
      // The backend's practiceIds-length-must-be-1 guard (leadController.js) reads
      // lead.PracticeId off EVERY row — without this, every submission from this page would be
      // rejected with "All uploaded leads must belong to exactly one practice".
      const leadsArrayWithPracticeId = leadsArray.map((lead) => ({ ...lead, PracticeId }));
      const response = await usersPostRequest(token, "/api/LeadGenerationUploadLead", {
        leadsArray: leadsArrayWithPracticeId,
        licenseType: outBoundLeadsDetail,
        leadsAllowed: computeLeadsAllowed(),
      });

      if (response === "Token is expired, login again") {
        setResultMsg("Session expired — please log in again.");
        return;
      }

      if (response?.message === "Data inserted successfully!") {
        const skipped = response.skippedRows || [];
        // droppedFieldsByRow is a newer field the backend may not yet send — treat absent/malformed
        // as "nothing dropped" rather than erroring.
        const droppedByRow = Array.isArray(response.droppedFieldsByRow)
          ? response.droppedFieldsByRow
          : [];

        const messages = [];
        messages.push(
          skipped.length > 0
            ? `Saved with ${skipped.length} row(s) skipped: ${skipped
                .map((s) => `row ${s.index + 1} (${s.reason})`)
                .join("; ")}`
            : "Leads saved successfully.",
        );
        if (droppedByRow.length > 0) {
          messages.push(
            `${droppedByRow.length} row(s) had unrecognized field(s) ignored: ${droppedByRow
              .map((d) => `row ${d.index + 1} (${(d.droppedKeys || []).join(", ")})`)
              .join("; ")}`,
          );
        }
        setResultMsg(messages.join(" "));
        getUserLicenseDetails(usersPostRequest, token, PracticeId, () => {}, setOutBoundLeadsDetail);
      } else if (response?.message === "You have reached the limit to upload leads on trial") {
        setResultMsg(response.message);
      } else {
        setResultMsg("Failed to save leads.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div>Loading…</div>;
  }

  if (!hasSchema) {
    return (
      <div>
        <div className="alert alert-warning">
          No schema defined for this practice yet. Define one below before adding or uploading
          leads.
        </div>
        <PracticeSchemaEditor onPublish={handleSchemaPublished} />
      </div>
    );
  }

  if (providerData.length === 0) {
    return (
      <div className="alert alert-warning">
        No provider added — please add a provider before uploading/adding leads.
      </div>
    );
  }

  return (
    <div>
      <h4>Manage Leads</h4>
      {resultMsg && <div className="alert alert-info">{resultMsg}</div>}

      <DynamicLeadForm
        providerData={providerData}
        schemaFields={schemaFields}
        leadsAllowed={computeLeadsAllowed()}
        onSubmit={submitLeads}
        submitting={submitting}
      />

      <hr className="my-4" />

      <DynamicBulkUpload
        providerData={providerData}
        schemaFields={schemaFields}
        onSubmit={submitLeads}
        submitting={submitting}
      />

      <hr className="my-4" />
      {showSchemaEditor ? (
        <PracticeSchemaEditor onPublish={handleSchemaPublished} />
      ) : (
        <p className="text-muted">
          Need to change what fields this practice tracks?{" "}
          <button className="btn btn-link p-0" onClick={() => setShowSchemaEditor(true)}>
            Manage schema
          </button>
        </p>
      )}
    </div>
  );
};
